import { useState, useEffect, useRef, useCallback, memo, useMemo, Component } from 'react';
import { 
  X, ChevronLeft, TrendingUp, TrendingDown, Star, Lock, Clock,
  Plus, Minus, AlertCircle, Info, RefreshCw, ChevronDown, Check,
  CandlestickChart, Search
} from 'lucide-react';
import { useTraderText, useTraderLanguage } from '../context/TraderLanguageContext';
import { useTraderTheme } from '../context/TraderThemeContext';
import { useTraderAuth } from '../context/TraderAuthContext';
import { useWebSocket } from '../../context/WebSocketContext';
import { scopedQuote } from '../utils/quotes';
import { isBinancePair } from '../utils/binance';
import { useMarketStatus } from '../utils/marketHours';
import { useOrderPreview } from '../hooks/useOrderPreview';
import BrokerChart from './BrokerChart';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Formats live price into MetaTrader 5 Typography:
// Base (e.g. "5.50"), Big Pips (e.g. "96"), and Pipette Superscript (e.g. "9")
export const splitMT5Price = (priceVal, symbol = '') => {
  if (priceVal === undefined || priceVal === null || isNaN(priceVal) || priceVal === 0) {
    return { base: '0.00', pips: '00', pipette: '' };
  }
  const num = Number(priceVal);
  let s = '';
  if (num > 1000) {
    s = num.toFixed(2);
  } else if (num > 10) {
    s = num.toFixed(2);
  } else {
    s = num.toFixed(5);
  }

  const parts = s.split('.');
  const integerPart = parts[0];
  const decimals = parts[1] || '';

  if (decimals.length >= 5) {
    return {
      base: `${integerPart}.${decimals.slice(0, -3)}`,
      pips: decimals.slice(-3, -1),
      pipette: decimals.slice(-1)
    };
  } else if (decimals.length >= 3) {
    return {
      base: `${integerPart}.${decimals.slice(0, -2)}`,
      pips: decimals.slice(-2),
      pipette: ''
    };
  } else if (decimals.length === 2) {
    return {
      base: `${integerPart}.`,
      pips: decimals,
      pipette: ''
    };
  }
  return {
    base: s,
    pips: '',
    pipette: ''
  };
};

// Error Boundary for mobile view resilience
class MobileDetailErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    console.error('[MobileInstrumentDetail] Render error:', error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="p-6 text-center text-rose-400 bg-gray-950 min-h-screen flex items-center justify-center">
          <div>
            <AlertCircle size={40} className="mx-auto mb-2 text-rose-500" />
            <p className="font-bold text-sm mb-1">Trade View Error</p>
            <p className="text-xs text-gray-400 mb-4">{this.state.error?.message || 'Component failed to render'}</p>
            <button
              onClick={this.props.onClose}
              className="px-4 py-2 rounded-xl bg-cyan-500 text-sm font-semibold text-white hover:bg-cyan-600"
            >
              Back to Markets
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Main Mobile Instrument Detail Component with collapsible chart and MT5 Order Screen
const MobileInstrumentDetailInner = ({ 
  instrument, 
  onClose, 
  onOpenPosition,
  formatPrice,
  isFavorite,
  toggleFavorite,
  embedded = false,
  instruments = [],
  onSelectInstrument
}) => {
  const tx = useTraderText();
  const { locale, language } = useTraderLanguage();
  const { isDark } = useTraderTheme();
  const { user } = useTraderAuth();
  const { prices } = useWebSocket();

  // Multi-tier symbol lookup for real-time WebSocket feeds
  const sym = instrument?.symbol || 'EUR/USD';
  const cleanSym = sym ? sym.replace('/', '').toUpperCase() : '';
  const slashSym = (cleanSym.length === 6 && !sym.includes('/')) ? `${cleanSym.slice(0, 3)}/${cleanSym.slice(3)}` : sym;
  const livePriceQuote = sym ? (prices?.[sym] || (slashSym ? prices?.[slashSym] : null) || (cleanSym ? prices?.[cleanSym] : null)) : null;
  const priceData = scopedQuote(instrument, livePriceQuote) || livePriceQuote || {};

  const marketStatus = useMarketStatus(sym, instrument?.category, priceData?.next_open);
  const [showClosedModal, setShowClosedModal] = useState(false);
  const isMarketAvailable = marketStatus.isOpen || marketStatus.isCrypto;

  // Chart collapse state: DEFAULT STATE IS COLLAPSED (per user request)
  const [showChart, setShowChart] = useState(false);

  // Symbol Picker state (triggered by tapping symbol in header)
  const [showSymbolPicker, setShowSymbolPicker] = useState(false);
  const [symbolSearch, setSymbolSearch] = useState('');

  // Static price lock: freeze prices completely when market is closed
  const frozenPriceRef = useRef(null);
  useEffect(() => {
    frozenPriceRef.current = null;
  }, [sym]);

  useEffect(() => {
    if (isMarketAvailable) {
      frozenPriceRef.current = null;
    } else if (!frozenPriceRef.current) {
      const b = Number(priceData?.bid || instrument?.bid || 0);
      const a = Number(priceData?.ask || instrument?.ask || (b > 0 ? b * 1.0002 : 0));
      const c = Number(priceData?.change_pct ?? instrument?.change ?? 0);
      const h = Number(priceData?.high || (a > 0 ? a * 1.005 : b * 1.005 || 0));
      const l = Number(priceData?.low || (b > 0 ? b * 0.995 : 0));
      if (b > 0 || a > 0) {
        frozenPriceRef.current = { bid: b, ask: a, change: c, high: h, low: l };
      }
    }
  }, [isMarketAvailable, priceData, instrument]);

  // Parse bid, ask, and changes safely (strictly static if closed)
  const bid = (!isMarketAvailable && frozenPriceRef.current) 
    ? frozenPriceRef.current.bid 
    : Number(priceData?.bid || instrument?.bid || 0);
  const ask = (!isMarketAvailable && frozenPriceRef.current) 
    ? frozenPriceRef.current.ask 
    : Number(priceData?.ask || instrument?.ask || (bid > 0 ? bid * 1.0002 : 0));
  const change = (!isMarketAvailable && frozenPriceRef.current) 
    ? frozenPriceRef.current.change 
    : Number(priceData?.change_pct ?? instrument?.change ?? 0);
  const high = (!isMarketAvailable && frozenPriceRef.current && frozenPriceRef.current.high > 0) 
    ? frozenPriceRef.current.high 
    : Number(priceData?.high || (ask > 0 ? ask * 1.005 : bid * 1.005 || 0));
  const low = (!isMarketAvailable && frozenPriceRef.current && frozenPriceRef.current.low > 0) 
    ? frozenPriceRef.current.low 
    : Number(priceData?.low || (bid > 0 ? bid * 0.995 : 0));

  // Order state matching MetaTrader 5
  const [orderType, setOrderType] = useState(!isMarketAvailable ? 'pending' : 'market');
  const [showOrderTypeDropdown, setShowOrderTypeDropdown] = useState(false);
  const [size, setSize] = useState('1.00'); // Default to 1.00 matching screenshot
  const [pendingPrice, setPendingPrice] = useState('');
  
  // Stop Loss & Take Profit (MT5 inline step & editable)
  const [stopLoss, setStopLoss] = useState('');
  const [takeProfit, setTakeProfit] = useState('');
  const [isEditingSL, setIsEditingSL] = useState(false);
  const [isEditingTP, setIsEditingTP] = useState(false);

  // Fill Policy
  const [fillPolicy, setFillPolicy] = useState('Fill or Kill');
  const [showFillPolicyDropdown, setShowFillPolicyDropdown] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [showSpecsModal, setShowSpecsModal] = useState(false);

  // Ensure size is parsed properly for calculations
  const numericSize = useMemo(() => {
    if (typeof size === 'string') {
      const parsed = parseFloat(size);
      return isNaN(parsed) ? 1.00 : parsed;
    }
    return Number(size) || 1.00;
  }, [size]);

  const contract = Number(instrument?.contract_size ?? instrument?.lot_size ?? 1);
  const leverage = Math.min(Number(instrument?.leverage_max || 100), Number(user?.leverage || 100));
  const coefficient = Number(instrument?.margin_coefficient ?? 1);
  const executionPrice = orderType === 'pending' ? (Number(pendingPrice) || ask || bid) : (ask || bid);

  // Live Margin & Pip Value preview hook
  const preview = useOrderPreview(Boolean(instrument?.symbol), {
    symbol: instrument?.symbol,
    side: 'buy',
    type: orderType,
    volume: numericSize,
    ...(orderType === 'pending' && Number(pendingPrice) > 0 ? { price: Number(pendingPrice) } : {}),
    stop_loss: Number(stopLoss) > 0 ? Number(stopLoss) : null,
    take_profit: Number(takeProfit) > 0 ? Number(takeProfit) : null,
  });

  const margin = preview.data?.additional_margin !== undefined 
    ? preview.data.additional_margin 
    : (numericSize * contract * (executionPrice || 1)) / (leverage || 1) * coefficient;

  const pipValue = preview.data?.pip_value !== undefined 
    ? preview.data.pip_value 
    : (numericSize * contract * ((instrument?.symbol || '').endsWith('JPY') ? 0.01 : 0.0001));

  const money = (val) => {
    if (val === undefined || val === null || isNaN(val)) return '—';
    const currency = user?.currency || 'USD';
    try {
      return Number(val).toLocaleString(locale || 'en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    } catch {
      return `${currency} ${Number(val).toFixed(2)}`;
    }
  };

  // Pip step size calculator for suggested SL/TP
  const pipStep = useMemo(() => {
    const s = instrument?.symbol || '';
    if (s.endsWith('JPY')) return 0.01;
    if (s.includes('BTC') || s.includes('ETH')) return 1.0;
    if (s.includes('.IS')) return 0.05;
    return 0.0001;
  }, [instrument?.symbol]);

  // Suggested SL and TP defaults
  const suggestedSL = useMemo(() => {
    const base = bid || ask;
    if (!base) return '';
    const diff = pipStep * 20;
    const target = base - diff;
    return target > 0 ? formatPrice(target, instrument?.symbol) : '';
  }, [bid, ask, pipStep, formatPrice, instrument?.symbol]);

  const suggestedTP = useMemo(() => {
    const base = ask || bid;
    if (!base) return '';
    const diff = pipStep * 30;
    const target = base + diff;
    return target > 0 ? formatPrice(target, instrument?.symbol) : '';
  }, [ask, bid, pipStep, formatPrice, instrument?.symbol]);

  // Quick Lot Stepper (-5, -1, +1, +5)
  const handleQuickLotStep = (step) => {
    let cur = numericSize;
    let next;
    if (Math.abs(step) === 5) {
      if (cur < 1) {
        next = step > 0 ? cur + 0.5 : Math.max(0.01, cur - 0.5);
      } else {
        next = step > 0 ? cur + 5 : Math.max(0.01, cur - 5);
      }
    } else {
      // step is -1 or +1
      if (cur <= 0.1) {
        next = step > 0 ? cur + 0.01 : Math.max(0.01, cur - 0.01);
      } else if (cur < 1) {
        next = step > 0 ? cur + 0.1 : Math.max(0.01, cur - 0.1);
      } else {
        next = step > 0 ? cur + 1 : Math.max(0.01, cur - 1);
      }
    }
    setSize((Math.round(next * 100) / 100).toFixed(2));
  };

  // Stop Loss Stepper
  const handleSLStep = (dir) => {
    const curVal = Number(stopLoss) || (bid ? bid - pipStep * 20 : 0);
    const nextVal = Math.max(0, curVal + dir * pipStep * 5);
    setStopLoss(formatPrice(nextVal, instrument?.symbol));
  };

  // Take Profit Stepper
  const handleTPStep = (dir) => {
    const curVal = Number(takeProfit) || (ask ? ask + pipStep * 30 : 0);
    const nextVal = Math.max(0, curVal + dir * pipStep * 5);
    setTakeProfit(formatPrice(nextVal, instrument?.symbol));
  };

  // Execute Order (Called directly by "Sell by Market" or "Buy by Market")
  const handleExecuteOrder = async (targetSide) => {
    if (!isMarketAvailable && orderType === 'market') {
      setShowClosedModal(true);
      return;
    }
    if (submitting) return;

    if (orderType === 'pending' && (!pendingPrice || Number(pendingPrice) <= 0)) {
      toast.error(tx('Please specify a valid pending price'));
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        instrument,
        side: targetSide,
        size: numericSize,
        orderType,
        pendingPrice: orderType === 'pending' ? Number(pendingPrice) : undefined,
        stopLoss: Number(stopLoss) > 0 ? Number(stopLoss) : null,
        takeProfit: Number(takeProfit) > 0 ? Number(takeProfit) : null,
      };
      await onOpenPosition(payload);
    } catch (err) {
      console.error('Submit order failed', err);
      toast.error(tx('Failed to place order'));
    } finally {
      setSubmitting(false);
    }
  };

  // MT5 Typography price splits
  const bidSplit = splitMT5Price(bid, instrument.symbol);
  const askSplit = splitMT5Price(ask, instrument.symbol);

  // Filtered instruments for symbol picker
  const filteredInstruments = useMemo(() => {
    if (!symbolSearch) return instruments.slice(0, 50);
    const q = symbolSearch.toLowerCase();
    return instruments.filter(i => 
      i.symbol?.toLowerCase().includes(q) || 
      i.name?.toLowerCase().includes(q)
    ).slice(0, 50);
  }, [instruments, symbolSearch]);

  if (!instrument) return null;

  return (
    <div 
      data-testid="mobile-instrument-detail" 
      data-embedded={embedded}
      className={`mt5-mobile-detail ${embedded ? 'relative h-full' : 'fixed inset-0 z-50'} w-full max-w-full min-w-0 flex flex-col ${
        isDark ? 'bg-[#0a0e17] text-white' : 'bg-white text-gray-900'
      }`}
    >
      {/* 1. Top Navigation Bar (Matching Screenshot Header) */}
      <div className={`flex items-center justify-between px-3 py-2 border-b shrink-0 ${
        isDark ? 'border-gray-800 bg-[#0f1724]' : 'border-gray-200 bg-white'
      }`}>
        {/* Back Button with Circle styling */}
        <button 
          onClick={onClose} 
          className={`w-9 h-9 rounded-full flex items-center justify-center transition-transform active:scale-95 ${
            isDark ? 'bg-gray-800/80 text-white hover:bg-gray-700' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
          }`}
          data-testid="close-detail"
          aria-label={tx("Back")}
        >
          <ChevronLeft size={20} />
        </button>

        {/* Center: Symbol with Chevron and Subtitle */}
        <button 
          onClick={() => instruments.length > 0 && setShowSymbolPicker(true)}
          className="text-center min-w-0 flex-1 px-2 py-0.5 rounded-lg active:scale-98 transition-all hover:bg-white/5"
        >
          <div className="flex items-center justify-center gap-1">
            <span className="font-bold text-base tracking-wide truncate">{instrument.symbol}</span>
            <ChevronDown size={16} className="text-gray-400" />
          </div>
          <p className={`text-[11px] truncate ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            {instrument.name || instrument.symbol}
          </p>
        </button>

        {/* Right Action Icons: Favorite & Specs (i) */}
        <div className="flex items-center gap-1 shrink-0">
          <button 
            onClick={() => setShowSpecsModal(true)}
            className={`w-8 h-8 rounded-full flex items-center justify-center ${
              isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-800'
            }`}
            title={tx("Specifications")}
          >
            <Info size={16} />
          </button>
          <button 
            data-testid="mobile-detail-favorite" 
            aria-label={tx("Favorite instrument")} 
            onClick={(e) => toggleFavorite && toggleFavorite(instrument.symbol, e)} 
            className="w-8 h-8 rounded-full flex items-center justify-center"
          >
            <Star 
              size={18} 
              className={isFavorite ? 'text-amber-400 fill-amber-400' : isDark ? 'text-gray-500' : 'text-gray-400'} 
            />
          </button>
        </div>
      </div>

      {/* 2. Interactive Chart Collapse / Expand Bar */}
      <div className={`px-4 py-2 flex items-center justify-between border-b shrink-0 ${
        isDark ? 'bg-gray-900/50 border-gray-800/80' : 'bg-gray-50 border-gray-200'
      }`}>
        <div className="flex items-center gap-2">
          <CandlestickChart size={16} className="text-cyan-400" />
          <span className={`text-xs font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            {tx(language === 'tr' ? 'Grafik' : 'Chart')}
          </span>
          <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${
            change >= 0 
              ? 'bg-emerald-500/15 text-emerald-400' 
              : 'bg-rose-500/15 text-rose-400'
          }`}>
            {change >= 0 ? '+' : ''}{change.toFixed(2)}%
          </span>
        </div>

        {/* Chart Toggle Button */}
        <button
          type="button"
          onClick={() => setShowChart(prev => !prev)}
          data-testid="toggle-chart-collapse-btn"
          className={`px-3 py-1 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-all active:scale-95 ${
            showChart 
              ? isDark ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-gray-200 text-gray-700'
              : 'bg-cyan-500/15 text-cyan-400 border border-cyan-500/30 hover:bg-cyan-500/25'
          }`}
        >
          <span>{showChart ? tx(language === 'tr' ? 'Grafiği Gizle ▴' : 'Hide Chart ▴') : tx(language === 'tr' ? 'Grafiği Göster ▾' : 'Show Chart ▾')}</span>
        </button>
      </div>

      {/* Collapsible Chart Canvas Container (Rendered ONLY when showChart is true) */}
      {showChart && (
        <div className="w-full shrink-0 relative bg-gray-950 border-b border-gray-800/80 overflow-hidden animate-fadeIn">
          <BrokerChart
            symbol={instrument.symbol}
            instrumentData={instrument}
            priceData={priceData}
            spread={Number(instrument.spread || 0)}
            isVirtual={Boolean(instrument.is_virtual)}
            height={220}
            compact={true}
          />
        </div>
      )}

      {/* Market Closed & Countdown Banner (if closed) */}
      {!marketStatus.isOpen && !marketStatus.isCrypto && (
        <div 
          onClick={() => setShowClosedModal(true)}
          className="mx-4 mt-3 p-3 rounded-xl bg-gradient-to-r from-rose-950/70 via-gray-900/95 to-gray-900 border border-rose-500/30 flex items-center justify-between shadow-lg cursor-pointer active:scale-98 transition-all shrink-0"
          data-testid="mobile-market-closed-banner"
        >
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-rose-500/20 border border-rose-500/30 flex items-center justify-center text-rose-400 shrink-0">
              <Clock size={16} className="animate-pulse" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-bold text-white flex items-center gap-1.5 truncate">
                <span>{marketStatus.marketName || tx("Piyasa")} {tx("Kapalı")}</span>
              </p>
              <p className="text-[11px] text-gray-400 truncate">
                {tx("Açılış:")} <span className="text-emerald-400 font-medium">{marketStatus.localOpenTimeStr}</span>
              </p>
            </div>
          </div>

          <div className="text-right shrink-0 pl-2">
            <span className="text-[9px] text-gray-400 block uppercase tracking-wider">{tx("Kalan Süre")}</span>
            <span className="font-mono font-bold text-xs text-cyan-400 bg-gray-950 px-2 py-0.5 rounded border border-cyan-500/30">
              {marketStatus.digitalCountdown}
            </span>
          </div>
        </div>
      )}

      {/* 3. Main Order Form (Matching the Uploaded Screenshot Layout) */}
      <div className="flex-1 overflow-y-auto min-h-0 w-full flex flex-col px-4 py-2 space-y-1">
        
        {/* Row 1: Order Execution Type Dropdown ("Market Execution ▾") */}
        <div className="relative py-2 border-b border-gray-800/80">
          <button
            type="button"
            onClick={() => setShowOrderTypeDropdown(prev => !prev)}
            data-testid="order-type-dropdown-toggle"
            className="w-full flex items-center justify-between text-left py-1"
          >
            <span className={`text-base font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {orderType === 'pending' 
                ? tx(language === 'tr' ? 'Bekleyen Emir' : 'Pending Order') 
                : tx(language === 'tr' ? 'Piyasa İntikali' : 'Market Execution')}
            </span>
            <ChevronDown size={18} className={`text-gray-400 transition-transform ${showOrderTypeDropdown ? 'rotate-180' : ''}`} />
          </button>

          {showOrderTypeDropdown && (
            <div className={`absolute top-full left-0 right-0 mt-1 rounded-xl shadow-2xl z-30 border overflow-hidden ${
              isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
            }`}>
              <button
                type="button"
                onClick={() => {
                  if (isMarketAvailable) {
                    setOrderType('market');
                  } else {
                    toast.info(tx('Market is currently closed. Using Schedule Trade / Pending Order.'));
                    setOrderType('pending');
                  }
                  setShowOrderTypeDropdown(false);
                }}
                className={`w-full px-4 py-3 text-left text-sm flex items-center justify-between border-b ${
                  isDark ? 'border-gray-800 hover:bg-gray-800' : 'border-gray-100 hover:bg-gray-50'
                } ${orderType === 'market' ? 'text-cyan-400 font-bold' : ''}`}
              >
                <span>{tx(language === 'tr' ? 'Piyasa İntikali' : 'Market Execution')} {!isMarketAvailable && `(${tx('Kapalı')})`}</span>
                {orderType === 'market' && <Check size={16} />}
              </button>

              <button
                type="button"
                onClick={() => {
                  setOrderType('pending');
                  if (!pendingPrice) setPendingPrice(String(ask || bid));
                  setShowOrderTypeDropdown(false);
                }}
                className={`w-full px-4 py-3 text-left text-sm flex items-center justify-between ${
                  isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-50'
                } ${orderType === 'pending' ? 'text-cyan-400 font-bold' : ''}`}
              >
                <span>{tx(language === 'tr' ? 'Bekleyen Emir (Schedule Trade)' : 'Pending Order')}</span>
                {orderType === 'pending' && <Check size={16} />}
              </button>
            </div>
          )}
        </div>

        {/* Row 2: Lot Size Stepper Row ("-5   -1   1.00   +1   +5") */}
        <div className="py-2 border-b border-gray-800/80">
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => handleQuickLotStep(-5)}
              className="text-[#38bdf8] font-bold text-base px-3 py-1 hover:bg-cyan-500/10 rounded-lg active:scale-95 transition-all"
            >
              -5
            </button>
            <button
              type="button"
              onClick={() => handleQuickLotStep(-1)}
              className="text-[#38bdf8] font-bold text-base px-3 py-1 hover:bg-cyan-500/10 rounded-lg active:scale-95 transition-all"
            >
              -1
            </button>

            {/* Editable Lot Size in Center */}
            <div className="min-w-[70px] text-center">
              <input
                type="text"
                inputMode="decimal"
                value={size}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '' || /^\d*\.?\d*$/.test(val)) {
                    setSize(val === '' ? '' : val);
                  }
                }}
                onBlur={() => {
                  const parsed = parseFloat(size);
                  if (isNaN(parsed) || parsed < 0.01) setSize('0.01');
                  else if (parsed > 100) setSize('100.00');
                  else setSize((Math.round(parsed * 100) / 100).toFixed(2));
                }}
                className={`w-20 text-center font-bold text-lg bg-transparent border-b border-transparent focus:border-cyan-400 focus:outline-none ${
                  isDark ? 'text-white' : 'text-gray-900'
                }`}
              />
            </div>

            <button
              type="button"
              onClick={() => handleQuickLotStep(1)}
              className="text-[#38bdf8] font-bold text-base px-3 py-1 hover:bg-cyan-500/10 rounded-lg active:scale-95 transition-all"
            >
              +1
            </button>
            <button
              type="button"
              onClick={() => handleQuickLotStep(5)}
              className="text-[#38bdf8] font-bold text-base px-3 py-1 hover:bg-cyan-500/10 rounded-lg active:scale-95 transition-all"
            >
              +5
            </button>
          </div>

          {/* Subtitle: Margin & Pip Value calculation */}
          <div className={`text-center text-[11px] pt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            <span>{tx("Teminat")} {money(margin)}</span>
            <span className="mx-1.5 opacity-40">·</span>
            <span>{tx("Pip Değeri")} {money(pipValue)}</span>
          </div>
        </div>

        {/* Row 3 (Conditional): Pending Price Row when orderType === 'pending' */}
        {orderType === 'pending' && (
          <div className="flex items-center justify-between py-2.5 border-b border-gray-800/80">
            <span className={`text-sm font-medium ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>
              {tx("Price")}
            </span>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setPendingPrice(prev => String(Math.max(0, (Number(prev) || executionPrice) - pipStep * 5)))}
                className="text-[#38bdf8] font-bold text-xl px-2 py-0.5 hover:bg-cyan-500/10 rounded active:scale-95"
              >
                -
              </button>
              <input
                type="number"
                step="any"
                value={pendingPrice}
                onChange={(e) => setPendingPrice(e.target.value)}
                placeholder={formatPrice(executionPrice, instrument.symbol)}
                className={`w-28 text-center font-mono font-bold text-sm bg-transparent border-b border-amber-400 focus:outline-none ${
                  isDark ? 'text-white' : 'text-gray-900'
                }`}
              />
              <button
                type="button"
                onClick={() => setPendingPrice(prev => String((Number(prev) || executionPrice) + pipStep * 5))}
                className="text-[#38bdf8] font-bold text-xl px-2 py-0.5 hover:bg-cyan-500/10 rounded active:scale-95"
              >
                +
              </button>
            </div>
          </div>
        )}

        {/* Row 4: Stop Loss Row ("Stop Loss   -   not set   +") */}
        <div className="flex items-center justify-between py-3 border-b border-gray-800/80">
          <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            {tx("Stop Loss")}
          </span>

          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => handleSLStep(-1)}
              className="text-[#38bdf8] font-bold text-2xl leading-none px-2 py-0.5 hover:bg-cyan-500/10 rounded active:scale-95 transition-all"
              aria-label="Decrease Stop Loss"
            >
              -
            </button>

            <div className="min-w-[90px] text-center">
              {isEditingSL ? (
                <input
                  type="number"
                  step="any"
                  autoFocus
                  value={stopLoss}
                  onChange={(e) => setStopLoss(e.target.value)}
                  onBlur={() => setIsEditingSL(false)}
                  placeholder={suggestedSL}
                  className={`w-24 text-center font-mono text-sm border-b border-cyan-400 bg-transparent focus:outline-none ${
                    isDark ? 'text-white' : 'text-gray-900'
                  }`}
                />
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    if (!stopLoss) setStopLoss(suggestedSL);
                    setIsEditingSL(true);
                  }}
                  className={`font-mono text-sm tracking-wide ${
                    stopLoss 
                      ? isDark ? 'text-white font-bold' : 'text-gray-900 font-bold' 
                      : isDark ? 'text-gray-500' : 'text-gray-400'
                  }`}
                >
                  {stopLoss ? formatPrice(Number(stopLoss), instrument.symbol) : tx("not set")}
                </button>
              )}
            </div>

            <button
              type="button"
              onClick={() => handleSLStep(1)}
              className="text-[#38bdf8] font-bold text-2xl leading-none px-2 py-0.5 hover:bg-cyan-500/10 rounded active:scale-95 transition-all"
              aria-label="Increase Stop Loss"
            >
              +
            </button>
          </div>
        </div>

        {/* Row 5: Take Profit Row ("Take Profit   -   not set   +") */}
        <div className="flex items-center justify-between py-3 border-b border-gray-800/80">
          <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            {tx("Take Profit")}
          </span>

          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => handleTPStep(-1)}
              className="text-[#38bdf8] font-bold text-2xl leading-none px-2 py-0.5 hover:bg-cyan-500/10 rounded active:scale-95 transition-all"
              aria-label="Decrease Take Profit"
            >
              -
            </button>

            <div className="min-w-[90px] text-center">
              {isEditingTP ? (
                <input
                  type="number"
                  step="any"
                  autoFocus
                  value={takeProfit}
                  onChange={(e) => setTakeProfit(e.target.value)}
                  onBlur={() => setIsEditingTP(false)}
                  placeholder={suggestedTP}
                  className={`w-24 text-center font-mono text-sm border-b border-cyan-400 bg-transparent focus:outline-none ${
                    isDark ? 'text-white' : 'text-gray-900'
                  }`}
                />
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    if (!takeProfit) setTakeProfit(suggestedTP);
                    setIsEditingTP(true);
                  }}
                  className={`font-mono text-sm tracking-wide ${
                    takeProfit 
                      ? isDark ? 'text-white font-bold' : 'text-gray-900 font-bold' 
                      : isDark ? 'text-gray-500' : 'text-gray-400'
                  }`}
                >
                  {takeProfit ? formatPrice(Number(takeProfit), instrument.symbol) : tx("not set")}
                </button>
              )}
            </div>

            <button
              type="button"
              onClick={() => handleTPStep(1)}
              className="text-[#38bdf8] font-bold text-2xl leading-none px-2 py-0.5 hover:bg-cyan-500/10 rounded active:scale-95 transition-all"
              aria-label="Increase Take Profit"
            >
              +
            </button>
          </div>
        </div>

        {/* Row 6: Fill Policy Row ("Fill Policy   Fill or Kill") */}
        <div className="relative flex items-center justify-between py-3 border-b border-gray-800/80">
          <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            {tx("Fill Policy")}
          </span>
          <button
            type="button"
            onClick={() => setShowFillPolicyDropdown(prev => !prev)}
            className={`text-sm font-medium flex items-center gap-1 ${
              isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <span>{tx(fillPolicy)}</span>
          </button>
        </div>

        {/* Row 7: Big Dual Live Prices ("5.5096⁹     5.5100⁹") */}
        <div className="pt-4 pb-2">
          <div className="grid grid-cols-2 gap-4">
            {/* Left Price (Sell / Bid) */}
            <div className="text-center font-mono text-[#ef4444]">
              <div className="flex items-baseline justify-center">
                <span className="text-xl font-medium tracking-tight">{bidSplit.base}</span>
                <span className="text-3xl font-bold leading-none">{bidSplit.pips}</span>
                {bidSplit.pipette && (
                  <sup className="text-base font-bold ml-0.5 -top-2">{bidSplit.pipette}</sup>
                )}
              </div>
            </div>

            {/* Right Price (Buy / Ask) */}
            <div className="text-center font-mono text-[#ef4444]">
              <div className="flex items-baseline justify-center">
                <span className="text-xl font-medium tracking-tight">{askSplit.base}</span>
                <span className="text-3xl font-bold leading-none">{askSplit.pips}</span>
                {askSplit.pipette && (
                  <sup className="text-base font-bold ml-0.5 -top-2">{askSplit.pipette}</sup>
                )}
              </div>
            </div>
          </div>

          {/* Row 8: Big Action Execution Buttons ("Sell by Market" vs "Buy by Market") */}
          <div className="grid grid-cols-2 gap-3 mt-3">
            {/* Sell Button */}
            <button
              type="button"
              onClick={() => handleExecuteOrder('sell')}
              disabled={submitting || (!isMarketAvailable && orderType === 'market')}
              data-testid="mt5-sell-button"
              className={`py-3.5 px-4 rounded-xl font-bold text-base transition-all active:scale-98 flex items-center justify-center gap-1.5 shadow-md ${
                submitting || (!isMarketAvailable && orderType === 'market')
                  ? 'bg-rose-950/40 text-rose-300/40 cursor-not-allowed opacity-60'
                  : 'bg-[#e05243] hover:bg-[#d44334] text-white shadow-rose-900/30'
              }`}
            >
              {submitting ? (
                <RefreshCw size={18} className="animate-spin" />
              ) : !isMarketAvailable && orderType === 'market' ? (
                <span className="flex items-center gap-1 text-xs">
                  <Lock size={14} /> {tx('Sell (Closed)')}
                </span>
              ) : orderType === 'pending' ? (
                tx(language === 'tr' ? 'Satış Emri Gir' : 'Sell Limit')
              ) : (
                tx(language === 'tr' ? 'Piyasa Fiyatından Sat' : 'Sell by Market')
              )}
            </button>

            {/* Buy Button */}
            <button
              type="button"
              onClick={() => handleExecuteOrder('buy')}
              disabled={submitting || (!isMarketAvailable && orderType === 'market')}
              data-testid="mt5-buy-button"
              className={`py-3.5 px-4 rounded-xl font-bold text-base transition-all active:scale-98 flex items-center justify-center gap-1.5 shadow-md ${
                submitting || (!isMarketAvailable && orderType === 'market')
                  ? 'bg-blue-950/40 text-blue-300/40 cursor-not-allowed opacity-60'
                  : 'bg-[#3b82f6] hover:bg-[#2563eb] text-white shadow-blue-900/30'
              }`}
            >
              {submitting ? (
                <RefreshCw size={18} className="animate-spin" />
              ) : !isMarketAvailable && orderType === 'market' ? (
                <span className="flex items-center gap-1 text-xs">
                  <Lock size={14} /> {tx('Buy (Closed)')}
                </span>
              ) : orderType === 'pending' ? (
                tx(language === 'tr' ? 'Alış Emri Gir' : 'Buy Limit')
              ) : (
                tx(language === 'tr' ? 'Piyasa Fiyatından Al' : 'Buy by Market')
              )}
            </button>
          </div>

          {/* Row 9: Attention Disclaimer Text */}
          <p className={`text-[11px] text-center mt-3 px-2 leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            {tx("Attention! The trade will be executed at market conditions, difference with requested price may be significant!")}
          </p>
        </div>

      </div>

      {/* Symbol Switcher Modal (Triggered by tapping symbol in header) */}
      {showSymbolPicker && (
        <div 
          className="fixed inset-0 z-[100] flex flex-col bg-black/80 backdrop-blur-sm animate-fadeIn"
          onClick={() => setShowSymbolPicker(false)}
        >
          <div 
            className={`w-full max-h-[85vh] mt-auto rounded-t-2xl flex flex-col border-t shadow-2xl ${
              isDark ? 'bg-[#0f1724] border-gray-800 text-white' : 'bg-white border-gray-200 text-gray-900'
            }`}
            onClick={e => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className={`p-4 border-b flex items-center justify-between ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
              <h3 className="font-bold text-base">{tx(language === 'tr' ? 'Enstrüman Seç' : 'Select Instrument')}</h3>
              <button 
                onClick={() => setShowSymbolPicker(false)}
                className="p-1 rounded-full text-gray-400 hover:text-white"
              >
                <X size={20} />
              </button>
            </div>

            {/* Search Input */}
            <div className="p-3 border-b border-gray-800/60">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-3 text-gray-400" />
                <input
                  type="text"
                  value={symbolSearch}
                  onChange={(e) => setSymbolSearch(e.target.value)}
                  placeholder={tx("Search symbol or name...")}
                  className={`w-full pl-9 pr-3 py-2 rounded-xl text-sm border focus:outline-none focus:ring-2 focus:ring-cyan-500 ${
                    isDark ? 'bg-gray-800/80 border-gray-700 text-white' : 'bg-gray-100 border-gray-300 text-gray-900'
                  }`}
                />
              </div>
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto p-2 space-y-1 max-h-[60vh]">
              {filteredInstruments.map((inst) => {
                const isCurrent = inst.symbol === instrument.symbol;
                return (
                  <button
                    key={inst.symbol}
                    onClick={() => {
                      if (onSelectInstrument) onSelectInstrument(inst);
                      setShowSymbolPicker(false);
                    }}
                    className={`w-full p-3 rounded-xl flex items-center justify-between text-left transition-colors ${
                      isCurrent 
                        ? 'bg-cyan-500/20 text-cyan-400 font-bold' 
                        : isDark ? 'hover:bg-gray-800 text-white' : 'hover:bg-gray-100 text-gray-900'
                    }`}
                  >
                    <div>
                      <span className="font-bold text-sm block">{inst.symbol}</span>
                      <span className={`text-xs block ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        {inst.name || inst.symbol}
                      </span>
                    </div>
                    {isCurrent && <Check size={18} className="text-cyan-400" />}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Specifications Modal */}
      {showSpecsModal && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-xs"
          onClick={() => setShowSpecsModal(false)}
        >
          <div 
            className={`w-full max-w-sm rounded-2xl overflow-hidden shadow-2xl border ${
              isDark ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900'
            }`}
            onClick={e => e.stopPropagation()}
          >
            <div className={`p-4 border-b flex justify-between items-center ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
              <div>
                <h3 className="font-bold text-base">{instrument.symbol}</h3>
                <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  {instrument.name || instrument.symbol}
                </p>
              </div>
              <button 
                onClick={() => setShowSpecsModal(false)}
                className="p-1 rounded-lg hover:bg-gray-800"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-4 space-y-2 text-xs">
              <div className="flex justify-between py-1 border-b border-gray-800/50">
                <span className="text-gray-400">{tx("Contract Size")}</span>
                <span className="font-mono font-bold">{instrument.contract_size || 1}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-gray-800/50">
                <span className="text-gray-400">{tx("Leverage")}</span>
                <span className="font-mono font-bold">1:{leverage}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-gray-800/50">
                <span className="text-gray-400">{tx("Min lot")}</span>
                <span className="font-mono font-bold">{instrument.min_lot || 0.01}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-gray-800/50">
                <span className="text-gray-400">{tx("Max lot")}</span>
                <span className="font-mono font-bold">{instrument.max_lot || 100}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-gray-800/50">
                <span className="text-gray-400">{tx("Trading Hours")}</span>
                <span className="font-semibold text-emerald-400">
                  {marketStatus.isCrypto ? '24/7' : (marketStatus.isOpen ? tx('Open') : tx('Closed'))}
                </span>
              </div>
            </div>

            <div className="p-3 bg-gray-800/50 text-center">
              <button
                onClick={() => setShowSpecsModal(false)}
                className="w-full py-2 rounded-xl bg-cyan-500 text-white font-bold text-xs"
              >
                {tx("Close")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Closed Market Popup Modal with Live Countdown */}
      {showClosedModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
          <div className={`w-full max-w-sm rounded-2xl border p-5 shadow-2xl relative flex flex-col items-center text-center ${
            isDark ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900'
          }`}>
            <button
              onClick={() => setShowClosedModal(false)}
              className="absolute top-3.5 right-3.5 p-1 rounded-full text-gray-400 hover:text-white hover:bg-gray-800 transition-colors"
            >
              <X size={18} />
            </button>

            <div className="w-12 h-12 rounded-full bg-rose-500/15 border border-rose-500/30 flex items-center justify-center text-rose-400 mb-3 shadow-inner">
              <Lock size={22} className="animate-pulse" />
            </div>

            <h3 className="text-base font-bold mb-1">
              {marketStatus.marketName || tx("Piyasa Kapalı")}
            </h3>
            <p className="text-xs text-rose-400 font-medium mb-3">
              {marketStatus.reason || tx("Bu enstrümanın işlem seansı kapalıdır")}
            </p>

            {/* Countdown Box */}
            <div className="w-full p-3 rounded-xl bg-gray-950/80 border border-cyan-500/30 mb-3 flex flex-col items-center">
              <span className="text-[11px] text-cyan-400 font-medium mb-1.5 flex items-center gap-1">
                <Clock size={12} className="animate-spin" style={{ animationDuration: '6s' }} />
                {tx("Piyasa Açılışına Kalan Süre")}
              </span>
              <div className="font-mono text-2xl font-bold text-cyan-300 tracking-wider">
                {marketStatus.digitalCountdown}
              </div>
              <p className="text-[11px] text-gray-400 mt-1">
                {marketStatus.countdownTextTR}
              </p>
            </div>

            {/* Local schedule */}
            <div className={`w-full p-2.5 rounded-lg text-xs space-y-1.5 mb-3 text-left ${
              isDark ? 'bg-gray-800/80' : 'bg-gray-100'
            }`}>
              <div className="flex justify-between">
                <span className="text-gray-400">{tx("Çalışma Saatleri:")}</span>
                <span className="font-semibold text-gray-300">{marketStatus.workingHours}</span>
              </div>
              <div className="flex justify-between pt-1 border-t border-gray-700/40">
                <span className="text-gray-400">{tx("Açılış Zamanı:")}</span>
                <span className="font-semibold text-emerald-400">{marketStatus.localOpenTimeStr}</span>
              </div>
            </div>

            <p className="text-[11px] text-gray-400 mb-4 leading-relaxed">
              {tx("Piyasa kapalıyken anlık emir verilemez ve fiyatlar dondurulmuştur. Dilerseniz piyasa açıldığında otomatik devreye girecek Bekleyen Emir verebilirsiniz.")}
            </p>

            <div className="w-full flex gap-2">
              <button
                type="button"
                onClick={() => {
                  setOrderType('pending');
                  if (!pendingPrice) setPendingPrice(String(ask || bid));
                  setShowClosedModal(false);
                }}
                className="flex-1 py-2.5 rounded-xl bg-cyan-600 hover:bg-cyan-500 text-white font-semibold text-xs transition-colors shadow-md"
              >
                {tx("Bekleyen Emir Seç")}
              </button>
              <button
                type="button"
                onClick={() => setShowClosedModal(false)}
                className="py-2.5 px-4 rounded-xl border border-gray-600 text-gray-300 text-xs hover:bg-gray-800 transition-colors"
              >
                {tx("Kapat")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Top-level exported component with ErrorBoundary wrapper
const MobileInstrumentDetail = (props) => {
  return (
    <MobileDetailErrorBoundary onClose={props.onClose}>
      <MobileInstrumentDetailInner {...props} />
    </MobileDetailErrorBoundary>
  );
};

export default memo(MobileInstrumentDetail);
